import { batch, effect, signal, untracked } from '@preact/signals';
import { type CategoryData } from 'service/category/model.ts';
import { type GetCachedCategoriesParams, getCategoriesCached } from 'service/category/resource';
import {
	type CategoryTree,
	type CategoryTreeCategory,
	type DisplayCategory,
} from 'components/modal/categories/signals.ts';
import { tokenize } from 'utils/tokenize.ts';
import type { BazarCategoryTreeCategory } from 'service/category/utils';
import { isSSR } from 'utils/isSSR';

export const CATEGORIES_DATA_ELEMENT_ID = 'memoizedCategories';
export function generateCategoryMap(categories: CategoryData[]): Record<number, CategoryTreeCategory> {
	const map: Record<number, CategoryTreeCategory> = {};

	for (const k of categories) {
		map[k.id] = {
			...k,
			children: [],
			parent: null,
			searchToken: tokenize(k.name),
		};
	}

	return map;
}

/**
 * Generates a tree from a flat map of categories. The map however must contain all categories with correct links
 * between children and parents (each child needs to have a valid parent, which means no parent or ID of a different
 * category, that is included inside the categoryMap).
 * @param categoryMap
 */
export function generateCategoryTree(categoryMap: Record<number, CategoryTreeCategory>): CategoryTree {
	const tree: Record<number, CategoryTreeCategory> = {};

	for (const k in categoryMap) {
		const category = categoryMap[k];

		if (!category) continue;

		if (!category.parentId) {
			tree[k] = category;
		} else {
			category.parent = categoryMap[category.parentId] ?? null;
			categoryMap[category.parentId]?.children.push(category);
		}
	}

	return tree;
}

export function getCategoryTreeLeaves(categoryTree: CategoryTreeCategory[]): CategoryTreeCategory[] {
	const leafs: CategoryTreeCategory[] = [];
	for (const category of categoryTree) {
		if (!category.children?.length) {
			leafs.push(category);
		} else {
			leafs.push(...getCategoryTreeLeaves(category.children));
		}
	}
	return leafs;
}

class MemoizedCategories {
	#categories: CategoryData[] = []; // Private access to categories that will not trigger a refresh, even if needed
	#categoriesMap: Record<number, CategoryTreeCategory> = {};
	#categoriesTree: CategoryTree = {};
	#categoriesSeoSet: Set<string> = new Set();
	timestamp: number = Date.now();

	constructor() {
		this.initFromElement();
	}

	private refresh = (params?: GetCachedCategoriesParams) => {
		if (Date.now() - this.timestamp >= 1000 * 60) {
			getCategoriesCached({ limit: '2000', sort: 'name', ...params }).then((categories) => {
				// If there is no data, request failed, so we will not update categories to empty list.
				if (categories.length === 0) return;

				this.#categories = categories;
				this.timestamp = Date.now();
				this.#categoriesMap = generateCategoryMap(this.#categories);
				this.#categoriesTree = generateCategoryTree(this.#categoriesMap);
				this.#categoriesSeoSet = new Set(this.#categories.map((category) => category.seoName));
			});
		}
	};

	async init(params?: GetCachedCategoriesParams) {
		// if initialized, do not initialize again
		if (this.#categories.length > 0) return this.#categories;

		const rawCategories = await getCategoriesCached({ limit: '2000', sort: 'name', ...params });
		this.#categories = rawCategories;
		this.timestamp = Date.now();
		this.#categoriesMap = generateCategoryMap(rawCategories);
		this.#categoriesTree = generateCategoryTree(this.#categoriesMap);
		this.#categoriesSeoSet = new Set(this.#categories.map((category) => category.seoName));

		return this.#categories;
	}

	initFromElement() {
		if (isSSR()) return;

		const categories = this.getCategoriesFromElement();

		if (categories) {
			this.#categories = categories;
			this.timestamp = Date.now();
			this.#categoriesMap = generateCategoryMap(this.#categories);
			this.#categoriesTree = generateCategoryTree(this.#categoriesMap);
			this.#categoriesSeoSet = new Set(this.#categories.map((category) => category.seoName));
		} else {
			this.init();
		}
	}

	getCategoriesAsText() {
		this.refresh();
		return JSON.stringify(this.#categories);
	}

	getCategoriesFromElement() {
		const element = globalThis?.document?.getElementById('memoizedCategories');

		if (element && element.textContent) {
			const categories = JSON.parse(element.textContent.replace('/*<![CDATA[*/', '').replace('/*]]>*/', ''));
			element.remove();

			return categories;
		}

		return null;
	}

	/**
	 * Gets the categories instantly, but will trigger a refresh in the background
	 */
	get categories() {
		this.refresh();
		return structuredClone(this.#categories);
	}

	get categoriesMap() {
		return structuredClone(this.#categoriesMap);
	}
	get categoriesTree() {
		return structuredClone(this.#categoriesTree);
	}
	get categoriesSeoSet() {
		return structuredClone(this.#categoriesSeoSet);
	}
}
export const memoizedCategories = new MemoizedCategories();
if (isSSR()) {
	await memoizedCategories.init();
}

export const categoriesData = signal<CategoryData[]>(memoizedCategories.categories);
const defaultDisplayCategories: DisplayCategory[] = Object.values(memoizedCategories.categoriesTree);
export const displayCategories = signal<DisplayCategory[]>(defaultDisplayCategories);
export const displayCategoriesSorted = signal<DisplayCategory[]>(
	defaultDisplayCategories.sort((a, b) => a.name.localeCompare(b.name, 'cs-CZ')),
);
export const categoriesMap = signal<Record<number, CategoryTreeCategory>>(memoizedCategories.categoriesMap);
export const categoriesTree = signal<CategoryTree>(memoizedCategories.categoriesTree);
export const activeCategory = signal<BazarCategoryTreeCategory | null>(null);

// run recomputation when categoriesData changes
effect(() => {
	batch(() => {
		const categories = categoriesData.value;
		// subscribe to the activeCategory signal
		const category = activeCategory.value;

		// prevent loop when signal is updated
		untracked(() => {
			const categoriesMap_ = generateCategoryMap(categories);
			categoriesMap.value = categoriesMap_;
			const categoriesTree_ = generateCategoryTree(categoriesMap_);
			categoriesTree.value = categoriesTree_;

			let displayCategories_;
			if (category) {
				displayCategories_ =
					category.children.length > 0 ? category.children : (category?.parent?.children ?? []);
			} else {
				displayCategories_ = Object.values(categoriesTree_);
			}
			// @ts-expect-error todo: due to the fact that this signal is used in multiple places, the types are not perfect. Homepage is the only page that requires searchToken to be present in the category, and it is guaranteed for that page
			displayCategories.value = displayCategories_;
			// @ts-expect-error todo: due to the fact that this signal is used in multiple places, the types are not perfect. Homepage is the only page that requires searchToken to be present in the category, and it is guaranteed for that page
			displayCategoriesSorted.value = displayCategories_.sort((a, b) => a.name.localeCompare(b.name, 'cs-CZ'));
		});
	});
});
